// AlertCard.jsx
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { recentActionText } from '../functions/recentOwnerAction'; // DAS-2951
import valueTrapBadge from '../functions/valueTrapBadge'; // DAS-3119
import earningsBadge from '../functions/earningsBadge'; // DAS-3194
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Typography,
  Button,
  Chip,
  Checkbox,
  IconButton,
  Box,
  Stack,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Tooltip,
  Paper
} from '@mui/material';
import {
  ArrowUpward,
  ArrowDownward,
  InfoOutlined,
  Close,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import {
  renderAlertEffectiveness,
  fetchAlertEffectiveness,
  fetchAlertSubtypeAdvisory,
  // DAS-2356: per-alert advisory must use the same user-tuned thresholds as the modal
  readAdvisoryThresholds,
} from '../functions/alertClauseService';
import { message } from 'antd';  // DAS-2463: Jira ticket notification
import api from '../functions/api';   // DAS-2350: for fire-and-forget auto-mark-seen
import getAlertContextData from '../functions/getAlertContextData';
import TickerContextDetails from '../components/TickerContextDetails';
import { urgencyValue, urgencyBand, urgencyColor, urgencyAccent } from '../utils/urgency'; // DAS-2532
import isRecentAlert from '../functions/isRecentAlert';
import { tickerAlphaChip } from '../functions/tickerAlphaChip'; // DAS-3289 / DAS-3317

const toNum = (v) => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v.replace(/[^\d.-]/g, ''));
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

// simple in-memory cache to avoid refetching sectors for the same ticker this session
// const sectorCache = new Map();
const normalizeTicker = (t) => (typeof t === 'string' ? t.trim().toUpperCase() : t);

// Confirmation Section Component
const ConfirmationSection = ({ percentage, text }) => {
  const [expanded, setExpanded] = useState(false);
  
  // Extract percentage from text if not provided
  const extractedPercentage = percentage || (() => {
    const match = text.match(/Confirmation of the alert \((\d+)% of affirmation clauses\):/);
    return match ? parseInt(match[1]) : 0;
  })();

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
          Confirmation {extractedPercentage}%
        </Typography>
        <IconButton 
          onClick={() => setExpanded(!expanded)}
          size="small"
          sx={{ color: 'success.main' }}
        >
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
      <Divider sx={{ mb: 2 }} />
      {expanded && (
        <Box sx={{ 
          backgroundColor: 'success.main', 
          p: 2, 
          borderRadius: 1,
          maxHeight: '200px',
          overflowY: 'auto'
        }}>
          {text.split('\n').map((line, i) => (
            <Typography key={i} paragraph sx={{ margin: '4px 0', color: 'common.white' }}>
              {line.startsWith('TRUE:') ? `✓ ${line.substring(5).trim()}` : line}
            </Typography>
          ))}
        </Box>
      )}
    </>
  );
};

// Contradiction Section Component
const ContradictionSection = ({ percentage, text }) => {
  const [expanded, setExpanded] = useState(false);
  
  // Extract percentage from text if not provided
  const extractedPercentage = percentage || (() => {
    const match = text.match(/Contradictive to the alert \((\d+)% of affirmation clauses\):/);
    return match ? parseInt(match[1]) : 0;
  })();

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'error.main' }}>
          Contradiction {extractedPercentage}%
        </Typography>
        <IconButton 
          onClick={() => setExpanded(!expanded)}
          size="small"
          sx={{ color: 'error.main' }}
        >
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
      <Divider sx={{ mb: 2 }} />
      {expanded && (
        <Box sx={{ 
          backgroundColor: 'error.main', 
          p: 2, 
          borderRadius: 1,
          maxHeight: '200px',
          overflowY: 'auto'
        }}>
          {text.split('\n').map((line, i) => (
            <Typography key={i} paragraph sx={{ margin: '4px 0', color: 'common.white' }}>
              {line.startsWith('FALSE:') ? `✗ ${line.substring(6).trim()}` : line}
            </Typography>
          ))}
        </Box>
      )}
    </>
  );
};

const AlertCard = ({
  alert,
  alertEffectiveness,
  onClose,
  onCheckboxChange,
  onFollowedUpChange,
  onReinvestmentCheck,
  onTickerClick,
  onOpenMarketAlerts,
  openFPDialog,
  conflictInfo,  // DAS-2470: { accumulate: number, harvest: number } or undefined
}) => {
  const initiationPrice = useMemo(() => toNum(alert?.price_at_initiation), [alert]);
  const currentPrice = useMemo(() => toNum(alert?.price_current), [alert]);

  const priceDiff = useMemo(() => {
    if (initiationPrice === null || currentPrice === null || initiationPrice === 0) return null;
    return ((currentPrice - initiationPrice) / initiationPrice) * 100;
  }, [initiationPrice, currentPrice]);

  // DAS-2685: click the Change value -> show how the ticker's listing-exchange
  // index moved since the alert fired (is the drop company-specific or broad?).
  // null = hidden, 'loading', or the endpoint result object.
  const [indexCtx, setIndexCtx] = useState(null);
  const onChangeClick = async () => {
    if (indexCtx) { setIndexCtx(null); return; }   // toggle off if already shown
    const tk = alert?.ticker;
    const since = alert?.epoch_initiation;
    if (!tk || !since) return;
    setIndexCtx('loading');
    try {
      const { data } = await api.get('/alert_index_context', { params: { ticker: tk, since_epoch: since } });
      setIndexCtx(data || { status: 'unavailable' });
    } catch {
      setIndexCtx({ status: 'error' });
    }
  };

  const isHarvest = (alert?.alert_type || '').toLowerCase() === 'harvest';
  const isAccumulate = (alert?.alert_type || '').toLowerCase() === 'accumulate';
  const isStepIn = (alert?.alert_type || '').toLowerCase() === 'step in';
  const isMonitor = (alert?.alert_type || '').toLowerCase() === 'monitor';
  const trapBadge = valueTrapBadge(alert); // DAS-3119
  const reportBadge = earningsBadge(alert); // DAS-3194

  // DAS-2463: Jira ticket creation state
  const [jiraModalOpen, setJiraModalOpen] = useState(false);
  const [jiraUserNotes, setJiraUserNotes] = useState('');
  const [jiraCreating, setJiraCreating] = useState(false);
  const [jiraDuplicates, setJiraDuplicates] = useState(null); // null = not checked, [] = clean, [...] = duplicates found
  const [jiraDuplicateConfirmed, setJiraDuplicateConfirmed] = useState(false);

  let priceColor = 'text.primary';
  if (priceDiff !== null) {
    if (isHarvest) {
      priceColor = priceDiff < 0 ? 'success.main' : priceDiff > 0 ? 'error.main' : 'text.primary';
    } else if (isAccumulate) {
      priceColor = priceDiff > 0 ? 'success.main' : priceDiff < 0 ? 'error.main' : 'text.primary';
    } else if (isStepIn || isMonitor) {
      priceColor = priceDiff > 0 ? 'success.main' : priceDiff < 0 ? 'error.main' : 'text.primary';
    }
  }

  const [alignmentOpen, setAlignmentOpen] = useState(false);
  const [fpOpen, setFpOpen] = useState(false);
  const [urgencyOpen, setUrgencyOpen] = useState(false);

  const alignmentScore = alert?.LLM_analysis_alignment_score;
  const alignmentExpl = alert?.LLM_analysis_alignment_explanation || 'No explanation provided.';
  const fpScore = alert?.LLM_false_positive_score;
  const fpExpl = alert?.LLM_false_positive_explanation || 'No explanation provided.';
  const finalConfidence = alert?.pf_final_confidence_score;
  const finalConfidenceExpl = alert?.pf_final_confidence_score_explanation || 'No explanation provided.';
  const opportunityFactor = alert?.opportunity_factor;
  // DAS-2532: overall urgency score (headline signal)
  const urgencyScore = urgencyValue(alert);
  const urgencyBandVal = urgencyBand(alert);
  const alphaChip = tickerAlphaChip(alert); // DAS-3317: sentence with a whole date, coloured by direction
  const urgencyExpl = alert?.urgency_score_explanation || 'No explanation provided.';
  // DAS-2832: the named rising risk this alert is offside against (DAS-2831 saves it).
  const collidingRisk =
    (alert?.market_fit_source === 'risk_sector' || alert?.market_fit_source === 'risk_region')
    && Number(alert?.market_fit_alignment) < 0
      ? alert?.market_fit_name
      : null;

  const alignmentColor =
    alignmentScore >= 4 ? 'success' : alignmentScore >= 2 ? 'warning' : 'error';

  const fpColor =
    fpScore >= 8 ? 'error' : fpScore >= 6 ? 'warning' : fpScore >= 3 ? 'default' : 'success';
  // DAS-2473: Color reflects score strength across 0-1000 range
  const finalConfNum = parseFloat(finalConfidence);
  const finalColor =
    !Number.isFinite(finalConfNum) ? 'text.secondary' :
    finalConfNum >= 400 ? 'success.main' : finalConfNum >= 200 ? 'warning.main' : 'error.main';
  const oppColor =
    opportunityFactor >= 0.7 ? 'success.main' : opportunityFactor >= 0.4 ? 'warning.main' : 'error.main';

  const seenChecked = alert?.seen === 1 || alert?.seen === true;
  const followedChecked = alert?.followed_up === 1 || alert?.followed_up === true;
  const category = alert?.Category || alert?.category || alert?.asset_category || 'N/A';

  // DAS-2350: auto-mark the alert as seen=1 the first time the card is opened.
  // Fire-and-forget POST — soft-fails on network error. The local Redux state
  // isn't optimistically updated, so seen=1 will appear on the next data
  // refresh. Only fires once per alert.ID change AND only when not already seen.
  // Never auto-touches followed_up — that stays user-controlled.
  useEffect(() => {
    if (!alert?.ID) return;
    if (seenChecked) return;
    api.post('/pf_alert_check_update', { id: alert.ID, seen: 1 }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert?.ID]);

  // DAS-2354: enrich the per-alert effectiveness card with regime split
  // (DAS-2347) + subtype advisory badge (DAS-2348). The current market regime
  // is read from the portfolio reducer's Redux state, same as AlertEffectiveness.jsx.
  // Fetches are independent — either failing is non-fatal (graceful degrade
  // to the plain effectiveness table).
  const currentRegime = useSelector(
    (state) => state?.portfolio?.data?.regime?.current ?? null
  );
  const [regimeData, setRegimeData] = useState(null);
  const [advisoryRows, setAdvisoryRows] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchAlertEffectiveness({ byRegime: true });
        if (!cancelled) setRegimeData(Array.isArray(rows) ? rows : null);
      } catch (e) {
        if (!cancelled) setRegimeData(null);
      }
    })();
    (async () => {
      try {
        // DAS-2356: pass the user's saved thresholds so this per-alert badge
        // is consistent with the global Advisory panel's flagged set.
        const rows = await fetchAlertSubtypeAdvisory(readAdvisoryThresholds());
        if (!cancelled) setAdvisoryRows(Array.isArray(rows) ? rows : null);
      } catch (e) {
        if (!cancelled) setAdvisoryRows(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);



  const [sector, setSector] = useState(alert?.sector ?? null);
const [context, setContext] = useState(null); // { sector, performance, sectorTrendMapped, ... }
const reqRef = useRef(0);

useEffect(() => {
  const ticker = normalizeTicker(alert?.ticker);
  if (!ticker) {
    setSector(null);
    setContext(null);
    return;
  }
  const id = ++reqRef.current;
  (async () => {
    const data = await getAlertContextData(ticker, alert?.sector ?? null);
    if (reqRef.current === id && data) {
      setSector(data.sector || 'N/A');
      setContext(data);
    }
  })();
}, [alert?.ticker, alert?.sector]);

  // Check if alert is recent (< 96 hours old) - DAS-1420
  const isRecent = isRecentAlert(alert);
  // DAS-2951: does this alert contradict a trade the owner just made?
  const das2951Transactions = useSelector(state => state?.portfolio?.data?.transactions);
  const recentAction = recentActionText(alert, das2951Transactions);

  // DAS-2091: Calculate alert age in days from epoch_initiation (oldest leg)
  const alertAgeDays = alert?.epoch_initiation
    ? Math.floor((Date.now() / 1000 - alert.epoch_initiation) / 86400)
    : null;
  // DAS-2701: a combination shows "<newest leg>d / <oldest leg>d" for its Age.
  const _isCombination = (alert?.alert_subtype || '').toLowerCase() === 'combination';
  const newestLegAgeDays = alert?.epoch_initiation_newest_leg
    ? Math.floor((Date.now() / 1000 - alert.epoch_initiation_newest_leg) / 86400)
    : null;
  const showSplitAge = _isCombination && newestLegAgeDays !== null;

  // DAS-2466: Staleness detection — flag alerts where the opportunity has largely passed
  // Reuses `opportunityFactor` already declared at line 200
  const oppNum = useMemo(() => {
    const v = parseFloat(opportunityFactor);
    return Number.isFinite(v) ? v : null;
  }, [opportunityFactor]);
  const isStale = useMemo(() => {
    if (oppNum !== null && oppNum < 0.30) return true;
    if (priceDiff !== null && Math.abs(priceDiff) > 30) return true;
    if (alertAgeDays !== null && alertAgeDays > 45) return true;
    return false;
  }, [oppNum, priceDiff, alertAgeDays]);

  const staleReason = useMemo(() => {
    if (!isStale) return null;
    const parts = [];
    if (priceDiff !== null && Math.abs(priceDiff) > 30) {
      parts.push(`${priceDiff > 0 ? '+' : ''}${priceDiff.toFixed(1)}% since trigger`);
    }
    if (alertAgeDays !== null && alertAgeDays > 45) {
      parts.push(`${alertAgeDays} days old`);
    }
    if (oppNum !== null && oppNum < 0.30) {
      parts.push(`opportunity factor ${oppNum.toFixed(2)}`);
    }
    return parts.join(', ');
  }, [isStale, priceDiff, alertAgeDays, oppNum]);

  // DAS-2463: Handle "Create Jira Ticket" button click
  const handleCreateJiraClick = async () => {
    setJiraUserNotes('');
    setJiraDuplicates(null);
    setJiraDuplicateConfirmed(false);
    setJiraModalOpen(true);

    // Check for existing open tickets about this alert's ticker
    try {
      const res = await api.post('/jira/search_alert_tickets', { ticker: alert.ticker });
      setJiraDuplicates(res.data?.tickets || []);
    } catch {
      setJiraDuplicates([]); // On error, proceed without duplicate check
    }
  };

  const handleJiraSubmit = async () => {
    setJiraCreating(true);
    try {
      const summary = `Alert: ${alert.ticker} — ${alert.alert_type} / ${alert.alert_subtype || 'combination'}`;
      const description = [
        `## Alert Details`,
        `- **Ticker:** ${alert.ticker}`,
        `- **Company:** ${alert.company || '—'}`,
        `- **Alert Type:** ${alert.alert_type}`,
        `- **Alert Subtype:** ${alert.alert_subtype || '—'}`,
        `- **Confidence Score:** ${alert.pf_final_confidence_score ?? '—'}`,
        `- **Alert ID:** ${alert.ID}`,
        '',
        `## Alert Text`,
        alert.pf_alert_text || '(no text)',
        '',
        jiraUserNotes ? `## User Notes\n${jiraUserNotes}` : '',
      ].filter(Boolean).join('\n');

      const res = await api.post('/jira/create_ticket', {
        summary,
        description,
        issue_type: 'Task',
        priority: 'Medium',
        labels: ['alert-action', alert.alert_type?.toLowerCase()].filter(Boolean),
      });

      if (res.data?.key) {
        message.success(
          <span>
            Created <a href={res.data.url} target="_blank" rel="noopener noreferrer">{res.data.key}</a>
          </span>
        );
        setJiraModalOpen(false);
      }
    } catch (err) {
      message.error(err.response?.data?.error || err.message || 'Failed to create ticket');
    } finally {
      setJiraCreating(false);
    }
  };

  return (
    <Card sx={{ 
      width: 650, 
      boxShadow: 3,
      // DAS-1420: Blue tint for fresh alerts (<96h), DAS-2466: amber tint for stale alerts
      backgroundColor: isRecent ? 'rgba(33, 150, 243, 0.08)' : isStale ? 'rgba(255, 152, 0, 0.06)' : 'background.paper',
      border: isRecent ? '1px solid rgba(33, 150, 243, 0.3)' : 'none', // Subtle blue border
      // DAS-2532: urgency accent bar (band colour) on the left edge
      borderLeft: urgencyBandVal ? `4px solid ${urgencyAccent(urgencyBandVal)}` : undefined
    }}>
      <CardHeader
        title={
          <Box display="flex" alignItems="center">
            <Typography 
              variant="h5" 
              component="div" 
              sx={{ 
                fontWeight: 'bold',
                cursor: onTickerClick ? 'pointer' : 'default',
                color: onTickerClick ? 'primary.main' : 'inherit',
                '&:hover': onTickerClick ? {
                  textDecoration: 'underline',
                  color: 'primary.dark'
                } : {}
              }}
              onClick={onTickerClick ? () => onTickerClick(alert?.ticker) : undefined}
            >
              {alert?.company} ({alert?.ticker})
            </Typography>
            <Chip
              label={alert?.alert_type}
              color={isHarvest ? 'error' : 'success'}
              size="medium"
              sx={{ 
                ml: 2,
                fontSize: '1rem',
                fontWeight: 'bold',
                height: '32px'
              }}
            />
            {trapBadge && (
              <Tooltip title={trapBadge.tooltip}>
                <Chip
                  data-testid="alert-value-trap-badge"
                  label={trapBadge.text}
                  color={trapBadge.color}
                  variant="outlined"
                  size="small"
                  sx={{ ml: 2, fontSize: '0.75rem' }}
                />
              </Tooltip>
            )}
            {reportBadge && (
              <Tooltip title={reportBadge.tooltip}>
                <Chip
                  data-testid="alert-earnings-badge"
                  label={reportBadge.text}
                  color={reportBadge.color}
                  variant="outlined"
                  size="small"
                  sx={{ ml: 2, fontSize: '0.75rem' }}
                />
              </Tooltip>
            )}
            {recentAction && (
              <Chip
                data-testid="das2951-recent-action"
                label={`⚠ ${recentAction}`}
                color="info"
                variant="outlined"
                size="small"
                sx={{ ml: 2, fontSize: '0.75rem' }}
              />
            )}
            {/* {sector && (
              <Chip
                label={sector}
                size="medium"
                sx={{ ml: 2, fontSize: '0.9rem' }}
                color="info"
              />
            )} */}
          </Box>
        }
        action={
          <IconButton onClick={onClose} aria-label="close" size="large">
            <Close fontSize="large" />
          </IconButton>
        }
      />

      <CardContent>
        {/* DAS-2695: Urgency — promoted to the TOP of the large card (was buried below
            the alert narrative). It's the single best "act on this now" pointer, so it's
            now the first thing seen. Click for the exact-calculation dialog; hover for the
            same breakdown as a tooltip. */}
        {urgencyBandVal && (
          <Paper
            elevation={3}
            onClick={() => setUrgencyOpen(true)}
            sx={{ p: 2, mb: 3, cursor: 'pointer', borderLeft: `8px solid ${urgencyAccent(urgencyBandVal)}` }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Chip label={urgencyBandVal} color={urgencyColor(urgencyBandVal)}
                    sx={{ fontWeight: 'bold', fontSize: '0.95rem', height: 30 }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                Urgency {urgencyScore != null ? Math.round(urgencyScore) : '—'}
              </Typography>
              <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{urgencyExpl}</span>} placement="right">
                <InfoOutlined fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
              {/* DAS-2832: a named rising risk working against this alert is the single
                  biggest reason the urgency number is low, so say it next to the number
                  instead of only inside the breakdown. Named risks only — a wind or the
                  overall lean would put a chip on nearly every card.
                  DAS-3317: the label wraps instead of being cut off. */}
              {collidingRisk && (
                <Tooltip placement="right"
                         title={`This ${isHarvest ? 'sell' : 'buy'} works against the rising risk "${collidingRisk}". Open Context Details for the full reason.`}>
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    data-testid="alert-risk-collision-chip"
                    label={`⚠ against: ${collidingRisk}`}
                    sx={{ ml: 'auto', maxWidth: '100%', height: 'auto', cursor: 'help',
                          '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
                  />
                </Tooltip>
              )}
            </Box>
            {/* DAS-3289: Ticker Alpha frozen at first scoring (the sixth urgency input).
                DAS-3317: its own full-width line under the number, not a narrow column beside it. */}
            {alphaChip && (
              <Typography data-testid="ticker-alpha-line" variant="body2"
                          sx={{ mt: 1, color: alphaChip.tone === 'default' ? 'text.secondary' : `${alphaChip.tone}.main` }}>
                {alphaChip.tooltip}
              </Typography>
            )}
          </Paper>
        )}

        {/* Price Information */}
        <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: '500' }}>Initiation Price</Typography>
            <Typography variant="h6">
              {initiationPrice !== null ? `$${initiationPrice.toFixed(2)}` : 'n/a'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: '500' }}>Current Price</Typography>
            <Typography variant="h6" sx={{ color: priceColor }}>
              {currentPrice !== null ? `$${currentPrice.toFixed(2)}` : 'n/a'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: '500' }}>Change</Typography>
            {/* DAS-2685: clickable -> reveals the listing-exchange index change since init */}
            <Typography
              variant="h6"
              onClick={priceDiff === null ? undefined : onChangeClick}
              title={priceDiff === null ? undefined : 'Show how this ticker’s exchange index moved since the alert fired'}
              sx={{
                color: priceColor, display: 'flex', alignItems: 'center', gap: 0.5,
                cursor: priceDiff === null ? 'default' : 'pointer',
                textDecoration: priceDiff === null ? 'none' : 'underline dotted',
                textUnderlineOffset: '3px',
              }}
            >
              {priceDiff === null ? 'n/a' : (
                <>
                  {priceDiff > 0 ? <ArrowUpward fontSize="small" /> : priceDiff < 0 ? <ArrowDownward fontSize="small" /> : null}
                  {Math.abs(priceDiff).toFixed(2)}%
                </>
              )}
            </Typography>
            {indexCtx === 'loading' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>loading index…</Typography>
            )}
            {indexCtx && indexCtx !== 'loading' && indexCtx.status === 'ok' && (
              <Box sx={{ mt: 0.25 }}>
                {[
                  indexCtx.index_pct != null && { label: indexCtx.index_name, pct: indexCtx.index_pct },
                  indexCtx.sector_pct != null && { label: `${indexCtx.sector} sector (${indexCtx.sector_etf})`, pct: indexCtx.sector_pct },
                ].filter(Boolean).map((b, i) => {
                  const rel = priceDiff !== null ? priceDiff - b.pct : null;
                  return (
                    <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
                      {b.label} {b.pct >= 0 ? '+' : ''}{b.pct}% since init
                      {rel !== null && (
                        <> · <Box component="span" sx={{ color: rel >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
                          {rel >= 0 ? '+' : ''}{rel.toFixed(2)}% rel
                        </Box></>
                      )}
                    </Typography>
                  );
                })}
              </Box>
            )}
            {indexCtx && indexCtx !== 'loading' && indexCtx.status && indexCtx.status !== 'ok' && (
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                index unavailable{indexCtx.exchange ? ` (${indexCtx.exchange})` : ''}
              </Typography>
            )}
          </Box>
          {/* DAS-2091 / DAS-2701: Alert age — combination shows newest / oldest leg */}
          {alertAgeDays !== null && (
            <Box>
              <Typography variant="body1" color="text.secondary" sx={{ fontWeight: '500' }}>Age</Typography>
              {showSplitAge ? (
                <Tooltip title={`Newest leg fired ${newestLegAgeDays}d ago · oldest leg ${alertAgeDays}d ago`}>
                  <Typography variant="h6" sx={{
                    color: newestLegAgeDays > 30 ? 'error.main' : newestLegAgeDays > 14 ? 'warning.main' : 'text.primary'
                  }}>
                    {newestLegAgeDays}d / {alertAgeDays}d
                  </Typography>
                </Tooltip>
              ) : (
                <Typography variant="h6" sx={{
                  color: alertAgeDays > 30 ? 'error.main' : alertAgeDays > 14 ? 'warning.main' : 'text.primary'
                }}>
                  {alertAgeDays} day{alertAgeDays !== 1 ? 's' : ''}
                </Typography>
              )}
            </Box>
          )}
        </Stack>

        {/* Category + Sector */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body1" component="span" sx={{ fontWeight: 'bold' }}>
            Category:
          </Typography>
          <Chip label={category} size="small" />
          {sector && (
            <>
              <Typography variant="body1" component="span" sx={{ fontWeight: 'bold', ml: 2 }}>
                Sector:
              </Typography>
              <Chip label={sector} size="small" color="info" />
            </>
          )}
          {/* {alert?.age_warning && (
            <Tooltip title={alert.age_warning}>
              <Box component="span" sx={{ color: 'error.main', ml: 1, fontSize: '1.5rem' }}>
                ⚠️
              </Box>
            </Tooltip>
          )} */}
        </Box>

        {/* Alert Type & Subtype - More Prominent Section */}
        <Paper elevation={2} sx={{ p: 2, mb: 3, backgroundColor: 'background.paper' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Alert Type</Typography>
              <Typography variant="h6" sx={{ color: isHarvest ? 'error.main' : 'success.main' }}>
                {alert?.alert_type}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Subtype</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    cursor: onOpenMarketAlerts ? 'pointer' : 'default',
                    color: onOpenMarketAlerts ? 'primary.main' : 'inherit',
                    '&:hover': onOpenMarketAlerts ? {
                      textDecoration: 'underline',
                      color: 'primary.dark'
                    } : {}
                  }}
                  onClick={onOpenMarketAlerts ? () => onOpenMarketAlerts(alert?.alert_subtype_identifier) : undefined}
                >
                  {alert?.alert_subtype || 'n/a'}
                </Typography>
                {alert?.alert_subtype_identifier && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
                    ({alert.alert_subtype_identifier})
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {/* Combination Details */}
          {alert?.alert_subtype === 'combination' && 
            typeof alert?.alert_combination_subtype_description === 'string' &&
            alert.alert_combination_subtype_description.trim() !== '' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Combination Details:</Typography>
              <List dense sx={{ py: 0 }}>
                {alert.alert_combination_subtype_description
                  .split(',')
                  .map((desc, idx) => (
                    <ListItem key={idx} sx={{ py: 0 }}>
                      <ListItemText primary={`• ${desc.trim()}`} primaryTypographyProps={{ variant: 'body1' }} />
                    </ListItem>
                  ))}
              </List>
            </Box>
          )}
        </Paper>

        {/* Analysis - More Prominent Section */}
        {/* Analysis - More Prominent Section */}
<Paper elevation={2} sx={{ p: 2, mb: 3, backgroundColor: 'background.paper' }}>
  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
    ALERT
    {alert?.age_warning && (
      <Tooltip title={alert.age_warning}>
        <Box component="span" sx={{ color: 'error.main', fontSize: '1.5rem', lineHeight: 1 }}>
          ⚠️
        </Box>
      </Tooltip>
    )}
  </Typography>
  <Divider sx={{ mb: 2 }} />
  <Box sx={{ 
    backgroundColor: 'action.hover', 
    p: 2, 
    borderRadius: 1,
    maxHeight: '300px',
    overflowY: 'auto'
  }}>
    {typeof alert?.pf_alert_text === 'string' && alert.pf_alert_text.trim() !== ''
      ? alert.pf_alert_text.split('\n').map((line, i) => (
          <Typography key={i} paragraph sx={{ margin: '4px 0' }}>
            {line.startsWith('-') ? `• ${line.substring(1).trim()}` : line}
          </Typography>
        ))
      : <Typography color="text.secondary">No analysis text</Typography>}
  </Box>
        </Paper>

        {/* DAS-2466: Staleness warning banner */}
        {isStale && (
          <Paper elevation={2} sx={{
            p: 2, mb: 3,
            backgroundColor: '#fff8e1',
            border: '1px solid #ffb74d',
            borderRadius: 1,
          }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#e65100' }}>
              ⚠️ Opportunity may have passed
            </Typography>
            <Typography variant="body2" sx={{ color: '#bf360c', mt: 0.5 }}>
              {staleReason}
            </Typography>
          </Paper>
        )}

        {/* DAS-2470: Conflict annotation — contradictory Accumulate/Harvest on same ticker */}
        {conflictInfo && (() => {
          const thisType = (alert?.alert_type || '').toLowerCase();
          const oppositeType = thisType === 'harvest' ? 'Accumulate' : thisType === 'accumulate' ? 'Harvest' : null;
          const oppositeConf = thisType === 'harvest' ? conflictInfo.accumulate : thisType === 'accumulate' ? conflictInfo.harvest : null;
          if (!oppositeType || !oppositeConf) return null;
          return (
            <Paper elevation={2} sx={{
              p: 2, mb: 3,
              backgroundColor: '#e8eaf6',
              border: '1px solid #7986cb',
              borderRadius: 1,
            }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#283593' }}>
                ⚠️ Conflicting signal
              </Typography>
              <Typography variant="body2" sx={{ color: '#1a237e', mt: 0.5 }}>
                This ticker also has a <strong>{oppositeType}</strong> alert (confidence {Math.round(oppositeConf)})
              </Typography>
            </Paper>
          );
        })()}

        {/* Weighted Confidence (between ALERT and Confirmation) */}
        {(finalConfidence !== null && finalConfidence !== undefined) && (
          <Paper elevation={2} sx={{ p: 2, mb: 3, backgroundColor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: finalColor }}>
                Weighted Confidence {finalConfidence}{alert?.pf_confidence_tier ? <span data-testid="das2953-confidence-tier"> — {alert.pf_confidence_tier}</span> : null}
              </Typography>
              {finalConfidenceExpl && (
                <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{`Range: 0 – 1,000 (base value 1–10 × confirmation 0–100%)\n\n${finalConfidenceExpl}`}</span>} placement="right">
                  <InfoOutlined fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              )}
            </Box>
          </Paper>
        )}

        {/* Confirmation Section */}
        {alert?.pf_confirmation_txt && alert.pf_confirmation_txt.trim() !== '' && (
          <Paper elevation={2} sx={{ p: 2, mb: 3, backgroundColor: 'background.paper' }}>
            <ConfirmationSection 
              percentage={alert.pf_confirmation_percentage || 0}
              text={alert.pf_confirmation_txt}
            />
          </Paper>
        )}

        {/* Contradiction Section */}
        {alert?.pf_contradiction_txt && alert.pf_contradiction_txt.trim() !== '' && (
          <Paper elevation={2} sx={{ p: 2, mb: 3, backgroundColor: 'background.paper' }}>
            <ContradictionSection 
              percentage={alert.pf_contradiction_percentage || 0}
              text={alert.pf_contradiction_txt}
            />
          </Paper>
        )}

        {/* Weighted Affirmation (if available) */}
        {(alert?.pf_weighted_confirmation_percentage !== null && alert?.pf_weighted_confirmation_percentage !== undefined) && (
          <Paper elevation={2} sx={{ p: 2, mb: 3, backgroundColor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                Weighted Confirmation {parseInt(alert.pf_weighted_confirmation_percentage, 10)}%
              </Typography>
              {alert?.pf_weighted_confirmation_percentage_explanation && (
                <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{alert.pf_weighted_confirmation_percentage_explanation}</span>} placement="right">
                  <InfoOutlined fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              )}
            </Box>
          </Paper>
        )}

        {/* Opportunity Factor (DAS-2191) */}
        {(opportunityFactor !== null && opportunityFactor !== undefined) && (
          <Paper elevation={2} sx={{ p: 2, mb: 3, backgroundColor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: oppColor }}>
                Opportunity {Math.round(opportunityFactor * 100)}%
              </Typography>
              <Tooltip title={
                <span style={{ whiteSpace: 'pre-line' }}>
                  {`Opportunity Factor: ${opportunityFactor} (0.00–1.00)\n\n${alert?.opportunity_factor_explanation || 'No breakdown available'}\n\nWeighted inputs: Data freshness (40%), Price follow-through (30%), Alert age (30%).\nInvalidators: price reversal >10% or institutional flow flip override the score.`}
                </span>
              } placement="right">
                <InfoOutlined fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
          </Paper>
        )}

        {/* DAS-2475: Clause Outperformance Score */}
        {alert?.clause_outperformance_score != null && (() => {
          const score = parseFloat(alert.clause_outperformance_score);
          if (!Number.isFinite(score)) return null;
          const color = score >= 0.75 ? 'success.main' : score >= 0.50 ? 'warning.main' : 'error.main';
          return (
            <Paper elevation={2} sx={{ p: 2, mb: 3, backgroundColor: 'background.paper' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color }}>
                  Fundamental Strength {Math.round(score * 100)}%
                </Typography>
                <Tooltip title={
                  <span style={{ whiteSpace: 'pre-line' }}>
                    {`Fundamental Strength: ${Math.round(score * 100)}%\n\nAverage sector percentile of the trigger clause fundamentals.\n\n${alert?.clause_outperformance_explanation || 'No breakdown available'}\n\n≥75%: Strong outperformance\n50-74%: Moderate\n<50%: Weak`}
                  </span>
                } placement="right">
                  <InfoOutlined fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
            </Paper>
          );
        })()}

        {/* DAS-2364: Capital-Fit tag. BUY-side only (Accumulate / Step In) —
            Harvest and Monitor alerts have these columns NULL by design.
            Three render states based on capital_fit_status:
              'fits'         → 🟢 green Chip with the reason string
              'does_not_fit' → 🟡 yellow Chip with the reason string
              'na'           → grey "N/A" Chip (manual / WenO mapped part, no cash table)
            NULL → no chip (Harvest / Monitor / pre-DAS-2364 alerts). */}
        {alert?.capital_fit_status && (
          <Paper elevation={2} sx={{ p: 2, mb: 3, backgroundColor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {alert.capital_fit_status === 'fits' && (
                <Chip
                  label={`🟢 Fits — ${alert.capital_fit_reason || ''}`}
                  color="success"
                  size="medium"
                  sx={{ fontWeight: 'bold' }}
                />
              )}
              {alert.capital_fit_status === 'does_not_fit' && (
                <Chip
                  label={`🟡 Does NOT fit — ${alert.capital_fit_reason || ''}`}
                  color="warning"
                  size="medium"
                  sx={{ fontWeight: 'bold' }}
                />
              )}
              {alert.capital_fit_status === 'na' && (
                <Chip
                  label={`N/A — ${alert.capital_fit_reason || ''}`}
                  size="medium"
                  sx={{ color: '#888' }}
                />
              )}
              <Tooltip title={
                <span style={{ whiteSpace: 'pre-line' }}>
                  {`Capital-Fit (DAS-2364): snapshot of "can I afford this position?" at alert-fire time.\n\nGreen = part's cash ≥ target slot size.\nYellow = part's cash < target slot size.\nN/A = ticker mapped to a part where cash isn't tracked (manual / WenO).\n\nDoes NOT influence alert ranking. Does NOT gate the swap engine (DAS-2358).\n\nAdjust display-side target slot in the Capital status banner at the top of the Alerts modal (settings ⚙).`}
                </span>
              } placement="right">
                <InfoOutlined fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
          </Paper>
        )}

        {/* Actions */}
        <Paper elevation={3} sx={{ p: 2, mb: 3, border: '2px solid #1976d2', backgroundColor: '#f5f5f5' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>ACTIONS</Typography>
          <Divider sx={{ mb: 2, borderColor: 'primary.main' }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!seenChecked}
                    onChange={() => onCheckboxChange(alert.ID, alert.seen, alert.followed_up)}
                    size="medium"
                    sx={{ 
                      '&.Mui-checked': {
                        color: 'primary.main'
                      }
                    }}
                  />
                }
                label={
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Seen
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!followedChecked}
                    onChange={() => onFollowedUpChange(alert.ID, alert.seen, alert.followed_up)}
                    size="medium"
                    sx={{ 
                      '&.Mui-checked': {
                        color: 'primary.main'
                      }
                    }}
                  />
                }
                label={
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Followed Up
                  </Typography>
                }
              />
            </Box>

            {isHarvest && alert?.opportunities === 1 && (
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  onReinvestmentCheck(alert.ticker);
                  onClose?.();
                }}
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  px: 3,
                  py: 1
                }}
              >
                RE-INVESTMENT CHECK
              </Button>
            )}

            {/* DAS-2463: Create Jira Ticket button */}
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => { e.stopPropagation(); handleCreateJiraClick(); }}
              sx={{ fontSize: '0.75rem' }}
            >
              Create Jira Ticket
            </Button>
          </Box>
        </Paper>

        <TickerContextDetails context={context} alert={alert} />   {/* DAS-2832 */}

        {/* Metrics */}
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>LLM METRICS</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {alignmentScore !== null && alignmentScore !== undefined && (
              <Chip
                icon={<InfoOutlined />}
                label={`Alignment: ${alignmentScore}/10`}
                color={alignmentColor}
                onClick={() => setAlignmentOpen(true)}
                sx={{ 
                  cursor: 'pointer',
                  fontSize: '1rem',
                  padding: '8px 12px'
                }}
              />
            )}

            {fpScore !== null && fpScore !== undefined && (
              <Chip
                icon={<InfoOutlined />}
                label={`FP Risk: ${fpScore}/10`}
                color={fpColor}
                onClick={() => setFpOpen(true)}
                sx={{ 
                  cursor: 'pointer',
                  fontSize: '1rem',
                  padding: '8px 12px'
                }}
              />
            )}
          </Box>
        </Paper>

        {/* Alert Effectiveness — DAS-2354 compact card (clean toggle, regime split, advisory) */}
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>ALERT EFFECTIVENESS</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box>
            {renderAlertEffectiveness(
              alertEffectiveness,
              alert?.alert_subtype_identifier,
              {
                currentRegime,
                regimeData,
                advisoryRows,
                cleanByDefault: true,
              }
            )}
          </Box>
        </Paper>
      </CardContent>

      {/* Alignment Dialog */}
      <Dialog open={alignmentOpen} onClose={() => setAlignmentOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Alignment Explanation</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" whiteSpace="pre-line">
            {alignmentExpl}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlignmentOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* False Positive Risk Dialog */}
      <Dialog open={fpOpen} onClose={() => setFpOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>False Positive Risk</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" whiteSpace="pre-line">
            {fpExpl}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFpOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* DAS-2532: Urgency exact-calculation dialog */}
      <Dialog open={urgencyOpen} onClose={() => setUrgencyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Urgency {urgencyScore != null ? Math.round(urgencyScore) : ''}{urgencyBandVal ? ` — ${urgencyBandVal}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" whiteSpace="pre-line">
            {urgencyExpl}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUrgencyOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* DAS-2463: Jira ticket creation — MUI Dialog (same library as parent, no z-index issues) */}
      <Dialog
        open={jiraModalOpen}
        onClose={() => setJiraModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { zIndex: 1500 } }}
      >
        <DialogTitle>Create Jira Ticket — {alert?.ticker}</DialogTitle>
        <DialogContent>
          {/* Duplicate warning */}
          {jiraDuplicates === null && (
            <Typography variant="body2" sx={{ mb: 2, p: 1, bgcolor: '#e3f2fd', borderRadius: 1 }}>
              Checking for existing tickets...
            </Typography>
          )}
          {jiraDuplicates?.length > 0 && !jiraDuplicateConfirmed && (
            <Paper elevation={0} sx={{ mb: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {jiraDuplicates.length} open ticket(s) found for {alert?.ticker}:
              </Typography>
              {jiraDuplicates.map(t => (
                <Typography key={t.key} variant="body2" sx={{ ml: 1 }}>
                  <a href={t.url} target="_blank" rel="noopener noreferrer">{t.key}</a>
                  {' — '}{t.summary} <em>({t.status})</em>
                </Typography>
              ))}
              <Button
                variant="outlined"
                size="small"
                onClick={() => setJiraDuplicateConfirmed(true)}
                sx={{ mt: 1 }}
              >
                Create anyway
              </Button>
            </Paper>
          )}

          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            The ticket will include the full alert details ({alert?.alert_type} / {alert?.alert_subtype}).
            Describe what you want to investigate or act on:
          </Typography>
          <textarea
            rows={5}
            placeholder="Describe your concern or action plan for this alert..."
            value={jiraUserNotes}
            onChange={e => setJiraUserNotes(e.target.value)}
            style={{
              width: '100%', padding: 10, fontSize: 14, fontFamily: 'inherit',
              border: '1px solid #ccc', borderRadius: 4, resize: 'vertical',
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJiraModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleJiraSubmit}
            disabled={jiraCreating || (jiraDuplicates?.length > 0 && !jiraDuplicateConfirmed)}
          >
            {jiraCreating ? 'Creating...' : 'Create Ticket'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default AlertCard;
